import _ from 'lodash';
import piction from '../utils/piction';
import appConfig from '../appConfig';
import { launchWindow, downloadFile } from './uiUtils';
import { addToWorkspace, moveBetweenWorkspaces, moveUMO } from '../actions/umo';
import { deselectAll } from '../actions/umoGallery';
import { addIntlError, addIntlInfo } from '../actions/messageDialog';


/**
 * case reducer for umo selections. used by workspacePreview and umoGallery
 * @param state
 * @param action
 * @returns {*}
 */
export const umoSelectCaseReducer = (state, action) => {
  let newState = state;

  action.umoIDList.forEach((umoID) => {
    const foundIndex = newState.get('selectedUMOIDs').indexOf(umoID);

    if (action.isSelect === undefined) {
      if (foundIndex > -1) {
        // exists in the list already so toggle off
        newState = newState.deleteIn(['selectedUMOIDs', foundIndex]);
      } else {
        // not in list, so toggle on
        newState = newState.set('selectedUMOIDs', newState.get('selectedUMOIDs').push(umoID));
      }
    } else if (action.isSelect && foundIndex < 0) {
      newState = newState.set('selectedUMOIDs', newState.get('selectedUMOIDs').push(umoID));
    } else if (!action.isSelect) {
      if (foundIndex > -1) {
        newState = newState.deleteIn(['selectedUMOIDs', foundIndex]);
      }
    }
  });

  return newState;
};

const getUMOParams = (umoList, searchParams) => {
  const params = [];
  // if a umoList exists in addition to the search params
  // it means a "select all results" was done, and a few umos were "unselected"
  // so those items needs to be excluded
  if (umoList && searchParams) {
    params.push({ SEARCH:
        _.isString(searchParams) ? searchParams : searchParams.toSearchQuery() });
    params.push({ EXCLUDE_UMO_ID: umoList });
  } else if (umoList) {
    params.push({ UMO_ID: umoList });
  } else {
    params.push({ SEARCH: _.isString(searchParams) ? searchParams : searchParams.toSearchQuery() });
  }
  return params;
};

const createExportURL = (urlPrefix, params) =>
  `${urlPrefix}?${_.map(params, value => (_.isArray(Object.values(value)[0]) ?
    Object.values(value)[0].map(valID => `${Object.keys(value)[0]}=${valID}`).join('&') :
    `${Object.keys(value)[0]}=${Object.values(value)[0]}`)).join('&')}&SURL=${piction.session.surl || ''}`;

/**
 * Creates the url for excel export
 */
export const createExcelExportURL = (umoList, isAllTags, searchParams) => {
  const params = [{ n: 'xls_image' },
    { SORT_ON: 'MAP' },
    ...getUMOParams(umoList, searchParams)];

  if (!isAllTags && appConfig.excelExportMetatags) {
    params.push({ META_TAG: appConfig.excelExportMetatags });
  }

  return createExportURL(piction.xmlURL, params);
};

/**
 * Creates the url for ppt export
 */
export const createPPTExportURL = (umoList, searchParams) => {
  const params = [
    { n: 'ppt_image' },
    { SORT_ON: 'MAP' },
    ...getUMOParams(umoList, searchParams)];
  return createExportURL(piction.xmlURL, params);
};

/**
 * Creates the url for ZIP download
 */
export const createZIPExportURL = (umoList, searchParams, options, isSelectAll) => {
  const defaultDerivs = [
    { image_type: 'PHOTO' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '2' },
    { image_type: 'VIDEO' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '2' },
    { image_type: 'AUDIO' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '1' },
    { image_type: 'DOCUMENT' },
    { DERIVATIVE: 'ORIGINAL' },
    { DNUM: '1' }];

  const params = [
    { n: 'zip_download' },
    { LBOX_SMART_CHECK: 'TRUE' },
    ...options || defaultDerivs,
  ];

  if (umoList && isSelectAll) {
    params.push({ SEARCH:
        _.isString(searchParams) ? searchParams : searchParams.toSearchQuery() });
    params.push({ EXCLUDE_UMO_ID: umoList });
  } else if (umoList) {
    params.push({ UMO_ID: umoList });
  } else {
    params.push({ SEARCH: _.isString(searchParams) ? searchParams : searchParams.toSearchQuery() });
  }

  return createExportURL(piction.xmlURL, params);
};

export const createUMOModURL = (action, params) => {
  const pars = [
    { n: 'NWS_IMAGE_PROCESS' },
    { ACTION: action },
    { DOWNLOAD: 'TRUE' },
    { ZIP_DOWNLOAD: 'TRUE' },
    { DOWNLOAD_IMAGE_ID: '1' },
    { DOWNLOAD_IMAGE: 'ORIGINAL' },
    { CREATE_NEW_UMO: 'FALSE' },
    ...params,
  ];
  return createExportURL(piction.jsonURL, pars);
};

/**
 * Creates the url for PDF download
 */
export const createPDFExportURL = (umoList, workspaceID, itemsPP, isSelectAll) => {
  const params = [
    { acid: '' },
    { ipp: itemsPP },
    { download_pdf: 'D' },
  ];

  if (umoList && isSelectAll) {
    params.push({ lbox_id: workspaceID });
    params.push({ EXCLUDE_UMO_ID: umoList });
  } else if (umoList) {
    params.push({ UMO_IDS: umoList.map(umoID => `${umoID}`).join(',') });
  } else {
    params.push({ lbox_id: workspaceID });
  }
  return createExportURL(`${piction.hostURL}cst.lightbox_report`, params);
};

/**
 * Not in use! has logic fail- only gets first (same) 250 umos. will only be good for testing.
 * retrieves bare data for umo call. used for queries that require umo ids
 * @param searchParams Can be a SearchParams instance or search query string
 */
export const retrieveUMOsMinimal = searchParams =>
  piction.getUMOs(_.isString(searchParams) ? searchParams : searchParams.toSearchQuery(), {
    CHECK_ALTERNATE_REPS: 'FALSE',
    FORCE_REFRESH: 'T',
    INCLUDE_MASTER_METADATA: 'FALSE',
    IS_ADVANCED: 'TRUE',
    MAXROWS: '250',
    METADATA: 'NONE',
    ORIGINAL_URLS: 'FALSE',
    RELATED_OBJECTS: 'FALSE',
    SHOW_MASTER_METADATA: 'FALSE',
    START: '0',
    ENABLE_ORIGINAL_ATTRIBS: 'FALSE',
    WEB_URLS: 'TRUE',
    FULL_URL: 'FALSE',
  });

/**
 * returns a force-download url from a umo url by adding force_download=T
 * leaves file name (f=filename) at the end of url- for old browser support
 * @param url - original umo url
 * @param name - alternative name if the url doesn't contain one
 * @param dispatch- for session validation check in launchWindow func
 */
export const downloadURL = (url, name, dispatch) => {
  const splitUrlName = _.split(url, '&f=');
  const fileName = splitUrlName[1] || name;
  const downloadUrl = `${splitUrlName[0]}&force_download=T&f=${fileName}`;

  // popupForDownloads is used when download links aren't working well,
  // and we want items to open in a new window, instead of the current tab
  if (appConfig.usePopupForDownloads) launchWindow(downloadUrl, dispatch);
  else downloadFile(downloadUrl, fileName);
};

export const savetoDropbox = (umoList, dispatch) => {
  const urls = umoList.map(item =>
    ({
      url: item.umo.webQuality ? item.umo.webQuality.url : item.umo.thumbnail.url,
      filename: item.umo.name,
    }));

  const options = {
    files: urls,
    success: () => { dispatch(addIntlInfo('searchResults.options.actions.dropboxSaveSuccess')); },
    // eslint-disable-next-line no-console
    progress: (progress) => { console.warn(progress); },
    cancel: () => {},
    // eslint-disable-next-line no-console
    error: (err) => { dispatch(addIntlError('searchResults.options.actions.dropboxSaveError')); console.warn(err); },
  };

  // eslint-disable-next-line no-undef
  Dropbox.save(options);
};

export const moveUmoHandler = (origin, target, umoList, dispatch, searchParams, isCopy) => {
  if (target.isWorkspace) {
    if (origin.isWorkspace) { // ws to ws
      dispatch(moveBetweenWorkspaces(
        origin.id,
        target.id,
        umoList,
        searchParams,
        isCopy
      ));
    } else { // cat to ws
      dispatch(addToWorkspace(
        target.id,
        umoList,
        searchParams,
        { isCopy },
      ));
    }
  } else if (!target.isWorkspace) {
    if (origin.isWorkspace) { // ws to cat
      dispatch(addIntlError('navCategoryPanel.workspaceToCategoryError'));
    } else if (target.editable) { // cat to cat
      // checks if target category has editing permissions
      dispatch(moveUMO(
        umoList,
        target.id,
        searchParams,
        isCopy
      ));
    } else {
      dispatch(addIntlError('navCategoryPanel.noEditRoleError'));
    }
  }
  dispatch(deselectAll());
};



// WEBPACK FOOTER //
// ./src/js/utils/umoUtils.js